/* eslint-disable camelcase */
import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { JobContext } from 'context/jobContextContainer';
import { tripMonitorRequestState, TRIP_MONITOR_TARGET_LABEL } from 'services/tripMonitorService';

// Alert for Trip Monitor events (geofence exit without the expected status
// change). Rendered inside JobTableSection's BannerStack above SmartMatchBanner,
// which handles positioning and spacing. Events with an in-flight app-fix
// request ("waiting") stay off the banner — the job row shows the updating
// state instead; if the driver's app doesn't respond, they come back here as
// "no response".
const TripMonitorBanner = () => {
  const { tripMonitorAlerts, setTripMonitorModalInfo } = useContext(JobContext);
  // Client-side snooze: hide an event's banner until page refresh (still reachable via its row pill).
  const [hiddenIds, setHiddenIds] = useState([]);

  const list = (Array.isArray(tripMonitorAlerts) ? tripMonitorAlerts : [])
    .filter(a => !hiddenIds.includes(a.id))
    .filter(a => tripMonitorRequestState(a) !== 'waiting');
  if (list.length === 0) {
    return null;
  }

  // No-response events outrank fresh ones; otherwise oldest first (API order).
  const timedOut = list.filter(a => tripMonitorRequestState(a) === 'timeout');
  const lead = timedOut.length > 0 ? timedOut[0] : list[0];
  const isTimeout = tripMonitorRequestState(lead) === 'timeout';
  const missed = lead.event_type === 'pickup_exit' ? 'Pick Up' : 'Drop Off';
  const stillLabel = lead.event_type === 'pickup_exit'
    ? (parseInt(lead.job_status, 10) < 4 ? 'still “Accepted”' : 'still “Arrived”')
    : 'still “Picked Up”';
  const where = lead.event_type === 'pickup_exit' ? 'pickup' : 'drop-off';
  const requestedLabel = TRIP_MONITOR_TARGET_LABEL[parseInt(lead.requested_status, 10)] || missed;
  const extra = list.length - 1;

  return (
    <Banner className={isTimeout ? 'timeout' : ''}>
      <div className="tm-banner-body">
        <div className="tm-banner-eyebrow">{isTimeout ? 'DRIVER APP NOT RESPONDING' : 'MISSED STATUS CHANGE'}</div>
        <div className="tm-banner-title">
          {isTimeout
            ? `Job #${lead.job_id} — ${requestedLabel} request not confirmed`
            : `Job #${lead.job_id} — vehicle left ${where}, ${stillLabel}`}
        </div>
        <div className="tm-banner-more">
          {isTimeout
            ? `Retry or call the driver${lead.driver_name ? ` (${lead.driver_name})` : ''}${extra > 0 ? ` · +${extra} more waiting` : ''}`
            : `Missed: ${missed}${lead.driver_name ? ` · Driver ${lead.driver_name}` : ''}${extra > 0 ? ` · +${extra} more waiting` : ''}`}
        </div>
      </div>
      <Button variant="contained" onClick={() => setTripMonitorModalInfo(lead)} className="tm-banner-btn">
        Review &amp; Fix
      </Button>
      <IconButton
        size="small"
        aria-label="dismiss"
        onClick={() => setHiddenIds([...hiddenIds, lead.id])}
        style={{ color: '#8aa0b6' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Banner>
  );
};

export default TripMonitorBanner;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 420px;
  max-width: 100%;
  padding: 14px 16px 14px 20px;
  border-radius: 12px;
  background: #0d1b2a;
  color: #e8eef5;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  border-left: 5px solid #ff8a3d;

  &.timeout {
    border-left-color: #e74c3c;
  }

  .tm-banner-eyebrow {
    font-size: 11px;
    letter-spacing: 1.5px;
    font-weight: 700;
    color: #ff8a3d;
  }

  &.timeout .tm-banner-eyebrow {
    color: #ff6b5b;
  }
  .tm-banner-title {
    font-size: 17px;
    font-weight: 700;
    margin-top: 2px;
  }
  .tm-banner-more {
    font-size: 12px;
    color: #8aa0b6;
    margin-top: 2px;
  }
  .tm-banner-body {
    flex: 1;
  }
  .tm-banner-btn {
    background: #2f6df6;
    color: #fff;
    white-space: nowrap;
  }
`;
